import { Chip, ChipPropsColorOverrides, Stack, Typography } from '@mui/material'
import TableCell from '@mui/material/TableCell'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import useDeepCompareEffect from 'use-deep-compare-effect'
import { useQueryParams } from 'use-query-params'
import { CustomTable } from '../../../Components/Table/CustomTable'
import { createTableHeadCells } from '../../../Components/Table/CustomTableHead'
import { ListPage } from '../../../Components/Table/ListPage'
import { DataProvider } from '../../../Core/DataProvider'
import { formatDate } from '../../../Core/FormatDate'
import { formatPrice } from '../../../Core/FormatPrice'
import { createQueryParam } from '../../../Core/QueryParams'
import { setUtil } from '../../../redux/UtilSlice'
import { formatPaymentMethod, OrderStatus, OrdersType } from '../Orders'

const RECOURCE = 'shop/orders'
const tableHeadCells = [
    createTableHeadCells('شناسه سفارش', 'id'),
    createTableHeadCells('نام', 'user_name'),
    createTableHeadCells('شماره تلفن', 'user_phone'),
    createTableHeadCells('وضعیت', 'status'),
    createTableHeadCells('محصولات', 'products'),
    createTableHeadCells('اکانت / اطلاعات', 'account_summary'),
    createTableHeadCells('قیمت نهایی', 'total_price'),
    createTableHeadCells('روش پرداخت', 'payment_method'),
    createTableHeadCells('تاریخ سفارش', 'created_at'),
]
const queryNames = [
    {
        field: 'id',
        label: 'شناسه سفارش',
        type: '',
        choice: [],
    },
    {
        field: 'user_phone',
        label: 'شماره تلفن',
        type: '',
        choice: [],
    },
    {
        field: 'account_name',
        label: 'اسم / عنوان اکانت',
        type: '',
        choice: [],
    },
    {
        field: 'email',
        label: 'ایمیل سفارش',
        type: '',
        choice: [],
    },
    {
        field: 'password',
        label: 'رمز عبور سفارش',
        type: '',
        choice: [],
    },
    {
        field: 'status',
        label: 'وضعیت',
        type: 'choice',
        choice: [
            { id: 'pending', label: 'درانتظار' },
            { id: 'waiting_for_approval', label: 'در انتظار تایید' },
            { id: 'processing', label: 'درحال پردازش' },
            { id: 'completed', label: 'تکمیل شده' },
            { id: 'canceled', label: 'لغو شده' },
        ],
    },
    {
        field: 'payment_method',
        label: 'روش پرداخت',
        type: 'choice',
        choice: [
            { id: 'online', label: 'آنلاین' },
            { id: 'bank_transfer', label: 'کارت به کارت' },
            { id: 'digipay', label: 'اقساطی (دیجی‌پی)' },
        ],
    },
    {
        field: 'start_date',
        label: 'تاریخ شروع',
        type: 'date',
    },
    {
        field: 'end_date',
        label: 'تاریخ پایان',
        type: 'date',
    },
]
export const statusCalc = (status: OrderStatus) => {
    if (status === 'processing') return 'secondary'
    else if (status === 'completed') return 'success'
    else if (status === 'pending' || status === 'waiting_for_approval') return 'warning'
    else return 'primary'
}
export const enToPer = (status: string) => {
    if (status === 'processing') return 'درحال پردازش'
    else if (status === 'completed') return 'تکمیل شده'
    else if (status === 'pending') return 'درانتظار'
    else if (status === 'waiting_for_approval') return 'در انتظار تایید'
    else return 'لغو شده'
}
export function OrdersList() {
    const [search, setSearch] = useQueryParams(createQueryParam(queryNames))
    const [loading, setLoading] = useState(false)
    const [orders, setOrders] = useState<{
        total_results: number
        data: OrdersType[]
    }>({
        total_results: 0,
        data: [],
    })
    const dispatch = useDispatch()

    const getInitialPageSize = () => {
        return (
            Number(search.page_size) ||
            Number(localStorage.getItem('admin_page_size_shop/orders')) ||
            Number(localStorage.getItem('admin_page_size')) ||
            50
        )
    }

    // On mount, restore saved page and ensure default 50 page_size
    useEffect(() => {
        const preferredSize = getInitialPageSize()
        const savedPage = Number(sessionStorage.getItem('admin_last_page_shop/orders'))
        setSearch((prev: any) => {
            const updated = {...prev}
            let changed = false
            if (!prev.page_size) {
                updated.page_size = preferredSize
                changed = true
            }
            if (!prev.page && savedPage && savedPage > 1) {
                updated.page = savedPage
                changed = true
            }
            return changed ? updated : prev
        })
    }, [])

    useEffect(() => {
        if (search.page) {
            sessionStorage.setItem('admin_last_page_shop/orders', String(search.page))
        }
        if (search.page_size) {
            localStorage.setItem('admin_page_size', String(search.page_size))
            localStorage.setItem('admin_page_size_shop/orders', String(search.page_size))
        }
    }, [search.page, search.page_size])

    const getOrders = async () => {
        setLoading(true)
        try {
            const queryToSend: Record<string, any> = {
                ...search,
                page_size: search.page_size || getInitialPageSize(),
            }
            let response = await DataProvider.get<{
                total_results: number
                results: OrdersType[]
            }>(`${RECOURCE}/`, queryToSend)

            if (response) {
                setOrders({
                    total_results: response.data.total_results,
                    data: response.data.results,
                })
            }
        } catch (error: any) {
            dispatch(setUtil({ open: true, message: error.message, severity: 'error' }))
        }
        setLoading(false)
    }
    useDeepCompareEffect(() => {
        getOrders()
    }, [search])

    const randomColor: ChipPropsColorOverrides = ['primary', 'secondary', 'success', 'warning']

    return (
        <ListPage
            header="مدیریت سفارشات"
            href={RECOURCE + '/create'}
            searchField="username"
            searchText="نام، تلفن، شناسه، ایمیل، رمز یا نام اکانت"
            setParams={setSearch}
            params={search}
            resource={RECOURCE}
            getList={getOrders}
            filtersData={queryNames}
            noCreate={true}
            defaultState={{
                status: '',
                username: '',
                createdAt: '',
                products: [
                    {
                        name: '',
                        price: 0,
                        image: '',
                    },
                ],
            }}
        >
            <CustomTable
                resource={RECOURCE}
                headCells={tableHeadCells}
                rows={orders.data}
                count={orders.total_results}
                getList={getOrders}
                params={search}
                setParams={setSearch}
                handleSort={getOrders}
                loading={loading}
                hasDetail={true}
                tableBodyCells={orders.data.map((item, index) => (
                    <>
                        <TableCell>
                            <Typography
                                variant="body2"
                                fontSize={{
                                    xs: '13px',
                                    sm: '14px',
                                    md: '16px',
                                    xl: '17px',
                                }}
                                sx={{
                                    whiteSpace: 'nowrap',
                                    fontWeight: 700,
                                }}
                            >
                                {item.id}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography
                                variant="body2"
                                fontSize={{
                                    xs: '13px',
                                    sm: '14px',
                                    md: '16px',
                                    xl: '17px',
                                }}
                                sx={{
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.user_full_name}
                            </Typography>
                        </TableCell>
                        <TableCell>
                            <Typography
                                variant="body2"
                                fontSize={{
                                    xs: '13px',
                                    sm: '14px',
                                    md: '16px',
                                    xl: '17px',
                                }}
                                sx={{
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.user_phone}
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Chip
                                label={enToPer(item.status)}
                                color={statusCalc(item.status)}
                                variant="filled"
                                sx={{
                                    fontSize: {
                                        xs: '12px',
                                        sm: '13px',
                                        md: '14px',
                                        xl: '15px',
                                    },
                                }}
                            />
                        </TableCell>
                        {/* <TableCell>
                            <Typography
                                variant="body2"
                                fontSize={{
                                    xs: '13px',
                                    sm: '14px',
                                    md: '16px',
                                    xl: '17px',
                                }}
                                sx={{
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.reason}
                            </Typography>
                        </TableCell> */}
                        <TableCell>
                            <Stack direction={'row'} gap={1}>
                                {item.product_names.split(',').map((product, index) => (
                                    <Chip
                                        key={index}
                                        label={product}
                                        color={randomColor[index]}
                                        variant="outlined"
                                        sx={{
                                            fontSize: {
                                                xs: '12px',
                                                sm: '13px',
                                                md: '14px',
                                                xl: '15px',
                                            },
                                        }}
                                    />
                                ))}
                            </Stack>
                        </TableCell>

                        <TableCell>
                            <Typography
                                variant="body2"
                                fontSize={{
                                    xs: '12px',
                                    sm: '13px',
                                    md: '14px',
                                    xl: '15px',
                                }}
                                sx={{
                                    whiteSpace: 'normal',
                                    maxWidth: '240px',
                                    color: item.account_summary ? 'text.primary' : 'text.disabled',
                                    direction: 'rtl',
                                    fontWeight: item.account_summary ? 500 : 400,
                                }}
                            >
                                {item.account_summary || '-'}
                            </Typography>
                        </TableCell>

                        <TableCell>
                            <Typography
                                variant="body2"
                                fontSize={{
                                    xs: '13px',
                                    sm: '14px',
                                    md: '16px',
                                    xl: '17px',
                                }}
                                sx={{
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatPrice(item.total_price)}
                            </Typography>
                        </TableCell>

                        <TableCell>
                            {item.payment_method && (
                                <Typography
                                    variant="body2"
                                    fontSize={{
                                        xs: '13px',
                                        sm: '14px',
                                        md: '15px',
                                        xl: '16px',
                                    }}
                                    sx={{
                                        whiteSpace: 'nowrap',
                                        fontWeight: item.payment_method.toLowerCase().includes('digipay') || item.payment_method.includes('اقساطی') ? 600 : 400,
                                        color: item.payment_method.toLowerCase().includes('digipay') || item.payment_method.includes('اقساطی') ? '#EF394E' : 'inherit'
                                    }}
                                >
                                    {formatPaymentMethod(item.payment_method)}
                                </Typography>
                            )}
                        </TableCell>
                        <TableCell>
                            <Typography
                                variant="body2"
                                fontSize={{
                                    xs: '13px',
                                    sm: '14px',
                                    md: '16px',
                                    xl: '17px',
                                }}
                                sx={{
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {formatDate(item.created_at)}
                            </Typography>
                        </TableCell>
                    </>
                ))}
            />
        </ListPage>
    )
}
